import AlternativeV2 from "@/components/Alternative/v2/AlternativeV2";
import GraphyVsAlternative from "@/components/Alternative/v2/GraphyVsAlternative";
import LearningManagementSystem from "@/components/LMS/v2/LearningManagementSystem";
import CreateAnimatedLP from "@/components/MarketingPage/CreateAnimatedLP";
import ErrorBoundary from "@/components/MarketingPage/ErrorBoundary";
import FeatureCTASection from "@/components/Features/FeatureCTASection";
import FeatureCardsSection from "@/components/Features/FeatureCardsSection";
import FeatureHeroSection from "@/components/Features/FeatureHeroSection";
import FeaturesGrid from "@/components/Home/FeaturesGrid/FeaturesGrid";
import GraphyAssist from "@/components/Home/GraphyAssist";
import ProductV2 from "@/components/Product/v2/ProductV2";
import CentreCtaSection from "@/components/ProductPage/CentreCtaSection";
import ProductHeroSection from "@/components/ProductPage/ProductHeroSection";
import ProductStepsSection from "@/components/ProductPage/ProductStepsSection";
import SuccessStoriesMarqueeSection, {
  SuccessStoriesMarqueeSectionPropType,
} from "@/components/organisms/Carousels/SuccessStoriesMarqueeSection";
import TestimonyCarouselSection, {
  TestimonyCarouselSectionPropType,
} from "@/components/organisms/Carousels/TestimonyCarouselSection";
import FaqSection from "@/components/organisms/FaqSection";
import { FooterDataType } from "@/components/organisms/Footer";
import GraphyHead from "@/components/organisms/GraphyHead";
import { Navbar } from "@/components/organisms/Navbar";
import OtherOfferingsSection, {
  OtherOfferingsMetaDataType,
  OtherOfferingsSectionPropType,
} from "@/components/organisms/OtherOfferingsSection";
import Feature from "@/components/templates/LandingPage/Feature";
import { DEFAULT_COUNTRY_CODE } from "@/shared/constants/common.constants";
import {
  AlternativeV2DataType,
  alternativeV2Data,
} from "@/shared/data/alternative.v2.data";
import { creatorStoriesPageData_en } from "@/shared/data/creator-stories.data";
import { creatorStoriesPageData_kr } from "@/shared/data/creator-stories.data.kr";
import { featureV2Data } from "@/shared/data/feature.v2.data";
import { featureV2DataUs } from "@/shared/data/feature.v2.data.us";
import {
  FeaturePageDataType,
  featurePagesData_en,
  otherFeatureOfferingsSectionProps_en,
} from "@/shared/data/features.data";
import {
  featurePagesData_kr,
  otherFeatureOfferingsSectionProps_kr,
} from "@/shared/data/features.data.kr";
import { footerData_en, footerData_us } from "@/shared/data/footer.data";
import { footerData_kr } from "@/shared/data/footer.data.kr";
import { NavbarDataType, navbarData_en, navbarData_us } from "@/shared/data/navbar.data";
import { navbarData_kr } from "@/shared/data/navbar.data.kr";
import {
  ProductV2DataType,
  productV2Data,
} from "@/shared/data/product.v2.data";
import { buildUsStoriesFromFolder, productV2DataUs } from "@/shared/data/product.v2.data.us";
import {
  ProductDataType,
  otherProductOfferingsSectionProps_en,
  productPagesData_en,
  productPagesData_us,
} from "@/shared/data/products.data";
import {
  otherProductOfferingsSectionProps_kr,
  productPagesData_kr,
} from "@/shared/data/products.data.kr";
import { useDataStore } from "@/shared/store/useDataStore";
import { useNavStore } from "@/shared/store/useNavStore";
import {
  AlternativePageSlugEnum,
  FeaturePageSlugEnum,
  ProductPageSlugEnum,
} from "@/shared/types/common.types";
import {
  getUTMFieldsFromLocalStorage,
  stringifyUTMParams,
} from "@/shared/utils/utm.util";
import {
  ENVIRONMENT,
  ROOT_HOST,
} from "@shared-constants/env/internalService.constants";
import axios from "axios";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import { CtaActionType } from "@/components/molecules/CtaButtonGroup";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { parseCookies } from "nookies";
import { useEffect } from "react";

const Footer = dynamic(() => import("@/components/organisms/Footer"));
const InfoAccordion = dynamic(
  () => import("@/components/organisms/InfoAccordion/InfoAccordion")
);

/**
 * Unified root-level slug dispatcher.
 *
 * Resolves `/<slug>` to one of: alternative-V2 page, special hub (graphy-vs-
 * alternative, learning-management-system), product (V2 or legacy), feature
 * (V2 or legacy), or a CMS-driven marketing page. Returns 404 otherwise.
 *
 * Priority order matters for slugs that appear in multiple data sets — e.g.
 * `community-chat-for-creators` and `ai-avatar` exist in both productV2Data
 * and the legacy feature data; the product V2 entry wins.
 */
type SlugPageProps =
  | {
      kind: "alternative-v2";
      alternativeData: AlternativeV2DataType;
      useHomeChrome: boolean;
    }
  | { kind: "graphy-vs-alternative"; locale?: string }
  | { kind: "lms"; locale?: string }
  | {
      kind: "product-v2";
      productV2: ProductV2DataType;
      navbarData: NavbarDataType;
      footerData: FooterDataType;
    }
  | {
      kind: "product-legacy";
      productPageData: ProductDataType;
      otherOfferingsSectionProps: OtherOfferingsSectionPropType;
      testimonyCarouselSectionProps: TestimonyCarouselSectionPropType;
      successStoriesMarqueeSectionProps: SuccessStoriesMarqueeSectionPropType;
      navbarData: NavbarDataType;
      footerData: FooterDataType;
    }
  | {
      kind: "feature-v2";
      featureV2: ProductV2DataType;
      navbarData: NavbarDataType;
      footerData: FooterDataType;
    }
  | {
      kind: "feature-legacy";
      featurePageData: FeaturePageDataType;
      otherOfferingsSectionProps: OtherOfferingsSectionPropType;
      testimonyCarouselSectionProps: TestimonyCarouselSectionPropType;
      successStoriesMarqueeSectionProps: SuccessStoriesMarqueeSectionPropType;
      navbarData: NavbarDataType;
      footerData: FooterDataType;
    }
  | {
      kind: "cms";
      pageName: string;
      pageData: { data: any[]; meta: string };
      langFile: string;
      navbarData: NavbarDataType;
      footerData: FooterDataType;
      locale: string;
    };

export const getServerSideProps: GetServerSideProps<SlugPageProps> = async (
  context
) => {
  const slug = context.params?.slug as string;
  const locale = (context.locale as "en" | "kr" | "us" | "in" | undefined) ?? "en";

  const navbarData = {
    en: navbarData_en,
    us: navbarData_us,
    kr: navbarData_kr,
    in: navbarData_en,
  }[locale];

  const footerData = {
    en: footerData_en,
    us: footerData_us,
    kr: footerData_kr,
    in: footerData_en,
  }[locale];

  // 1. Special hubs
  if (slug === "graphy-vs-alternative" && (locale === "en" || locale === "in")) {
    return { props: { kind: "graphy-vs-alternative" as const, locale } };
  }
  if ((slug === "learning-management-system" || slug === "learning-management-system-01") && (locale === "en" || locale === "in")) {
    return { props: { kind: "lms" as const, locale } };
  }

  // 2. Alternative V2 — handles both `<slug>-alternative` (canonical) and
  //    `<slug>-alternatives` (stripped-chrome variant).
  const isPluralAlternative = slug.endsWith("-alternatives");
  const altLookup = isPluralAlternative
    ? slug.replace(/-alternatives$/, "-alternative")
    : slug;
  const altData = alternativeV2Data[altLookup as AlternativePageSlugEnum];
  if (altData && (locale === "en" || locale === "us" || locale === "in")) {
    const bookDemoCtaUs = altData.bookDemoUrl
      ? {
          type: ButtonTypeEnum.Primary,
          size: ButtonSizeEnum.Regular,
          text: "Book a demo",
          actionType: CtaActionType.DelayedHrefBlank,
          href: `${altData.bookDemoUrl}?`,
        }
      : {
          type: ButtonTypeEnum.Primary,
          size: ButtonSizeEnum.Regular,
          text: "Book a demo",
          actionType: CtaActionType.BookDemo,
        };

    let finalAltData =
      locale === "us"
        ? {
            ...altData,
            hero: { ...altData.hero, primaryCta: bookDemoCtaUs },
            ctaBanner: {
              ...altData.ctaBanner,
              ctas: altData.ctaBanner.ctas.map((cta) =>
                cta.text?.toLowerCase().includes("demo")
                  ? { ...bookDemoCtaUs, text: cta.text }
                  : cta
              ),
            },
            sections: altData.sections.map((section) => {
              let updated: typeof section = section;
              if (section.kind === "stories") {
                updated = {
                  ...section,
                  data: {
                    ...section.data,
                    stories: buildUsStoriesFromFolder(
                      "Pricing Page/Pricing Page US images/Testimonial images"
                    ),
                    ctas: [bookDemoCtaUs],
                  },
                };
              }
              if (
                section.kind === "reviews-marquee-pain-points" ||
                section.kind === "dark-reasons" ||
                section.kind === "dark-pricing-compare" ||
                section.kind === "feature-compare-accordions"
              ) {
                updated = {
                  ...updated,
                  data: { ...(updated as any).data, primaryCta: bookDemoCtaUs },
                } as typeof section;
              }
              return updated;
            }),
          }
        : altData;

    return {
      props: {
        kind: "alternative-v2" as const,
        alternativeData: finalAltData,
        useHomeChrome: !isPluralAlternative && !altData.strippedChrome,
      },
    };
  }

  // 3. Product V2 — productV2Data wins over feature V2/legacy for shared
  //    slugs (community-chat-for-creators, ai-avatar).
  const productV2Source =
    locale === "us"
      ? productV2DataUs
      : locale !== "kr"
      ? productV2Data
      : undefined;
  const productV2Entry = productV2Source?.[slug as ProductPageSlugEnum];
  if (productV2Entry) {
    return {
      props: {
        kind: "product-v2" as const,
        productV2: productV2Entry,
        navbarData,
        footerData,
      },
    };
  }

  // 4. Product legacy
  const productPagesData = {
    en: productPagesData_en,
    us: productPagesData_us,
    kr: productPagesData_kr,
    in: productPagesData_en,
  }[locale];
  const productPageData = productPagesData.find(
    (p) => p.otherOfferingsMeta.slug === slug
  );
  if (productPageData) {
    const otherProductOfferingsSectionProps = {
      en: otherProductOfferingsSectionProps_en,
      us: otherProductOfferingsSectionProps_en,
      kr: otherProductOfferingsSectionProps_kr,
      in: otherProductOfferingsSectionProps_en,
    }[locale];
    const creatorStoriesPageData = {
      en: creatorStoriesPageData_en,
      us: creatorStoriesPageData_en,
      kr: creatorStoriesPageData_kr,
      in: creatorStoriesPageData_en,
    }[locale];

    const otherOfferingsMetaData: OtherOfferingsMetaDataType[] = [];
    productPagesData.forEach((p) => {
      if (p.otherOfferingsMeta.slug !== slug) {
        otherOfferingsMetaData.push(p.otherOfferingsMeta);
      }
    });

    const { creatorStoriesHeroSectionProps } = creatorStoriesPageData;
    const filteredTestimonies =
      creatorStoriesHeroSectionProps.successStories.filter(
        (s) => s.showOn.products?.[slug as ProductPageSlugEnum]?.testimony
      );
    const filteredSuccessStories =
      creatorStoriesHeroSectionProps.successStories.filter(
        (s) => s.showOn.products?.[slug as ProductPageSlugEnum]?.stories
      );

    return {
      props: {
        kind: "product-legacy" as const,
        productPageData,
        otherOfferingsSectionProps: {
          ...otherProductOfferingsSectionProps,
          otherOfferingsMetaData,
        },
        testimonyCarouselSectionProps: {
          ...productPageData.testimonyCarouselSectionProps,
          successStories: filteredTestimonies,
        },
        successStoriesMarqueeSectionProps: {
          ...productPageData.successStoriesMarqueeSectionProps,
          successStories: filteredSuccessStories,
        },
        navbarData,
        footerData,
      },
    };
  }

  // 5. Feature V2
  const featureV2Source =
    locale === "us"
      ? featureV2DataUs
      : locale !== "kr"
      ? featureV2Data
      : undefined;
  const featureV2Entry = featureV2Source?.[slug as FeaturePageSlugEnum];
  if (featureV2Entry) {
    return {
      props: {
        kind: "feature-v2" as const,
        featureV2: featureV2Entry,
        navbarData,
        footerData,
      },
    };
  }

  // 6. Feature legacy
  const featurePagesData = {
    en: featurePagesData_en,
    us: featurePagesData_en,
    kr: featurePagesData_kr,
    in: featurePagesData_en,
  }[locale];
  const featurePageData = featurePagesData.find(
    (f) => f.otherOfferingsMeta.slug === slug
  );
  if (featurePageData) {
    const otherFeatureOfferingsSectionProps = {
      en: otherFeatureOfferingsSectionProps_en,
      us: otherFeatureOfferingsSectionProps_en,
      kr: otherFeatureOfferingsSectionProps_kr,
      in: otherFeatureOfferingsSectionProps_en,
    }[locale];
    const creatorStoriesPageData = {
      en: creatorStoriesPageData_en,
      us: creatorStoriesPageData_en,
      kr: creatorStoriesPageData_kr,
      in: creatorStoriesPageData_en,
    }[locale];

    const otherOfferingsMetaData: OtherOfferingsMetaDataType[] = [];
    featurePagesData.forEach((f) => {
      if (f.otherOfferingsMeta.slug !== slug) {
        otherOfferingsMetaData.push(f.otherOfferingsMeta);
      }
    });

    const { creatorStoriesHeroSectionProps } = creatorStoriesPageData;
    const filteredTestimonies =
      creatorStoriesHeroSectionProps.successStories.filter(
        (s) => s.showOn.features?.[slug as FeaturePageSlugEnum]?.testimony
      );
    const filteredSuccessStories =
      creatorStoriesHeroSectionProps.successStories.filter(
        (s) => s.showOn.features?.[slug as FeaturePageSlugEnum]?.stories
      );

    return {
      props: {
        kind: "feature-legacy" as const,
        featurePageData,
        otherOfferingsSectionProps: {
          ...otherFeatureOfferingsSectionProps,
          otherOfferingsMetaData,
        },
        testimonyCarouselSectionProps: {
          ...featurePageData.testimonyCarouselSectionProps,
          successStories: filteredTestimonies,
        },
        successStoriesMarqueeSectionProps: {
          ...featurePageData.successStoriesMarqueeSectionProps,
          successStories: filteredSuccessStories,
        },
        navbarData,
        footerData,
      },
    };
  }

  // 7. CMS fetch — final fallback for marketing/landing pages
  const defaultLocale = context.defaultLocale;
  const isDefaultLocale = locale === defaultLocale;
  const localePageName = isDefaultLocale ? slug : `${slug}.${locale}`;
  const protocol = ENVIRONMENT === "development" ? "http" : "https";
  const cmsUrls = [
    `${protocol}://${ROOT_HOST}/api/marketing?page=${localePageName}`,
    `${protocol}://${ROOT_HOST}/api/marketing?page=${slug}`,
  ];

  let pageData = null;
  for (const url of cmsUrls) {
    try {
      const response = await axios.get(url);
      if (response?.data?.data) {
        pageData = response.data.data;
        break;
      }
    } catch (_e) {
      // try next URL
    }
  }

  if (pageData) {
    return {
      props: {
        kind: "cms" as const,
        pageName: slug,
        pageData,
        langFile: locale !== "en" ? slug : "",
        navbarData,
        footerData,
        locale,
      },
    };
  }

  return { notFound: true };
};

const SlugPage = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) => {
  const setShowSignUp = useNavStore((s) => s.setShowSignUp);
  const cookies = parseCookies();
  // Selector form — destructuring the whole store would re-render on every
  // unrelated change (e.g. `marketingPageSlug` updates we make below) and
  // instantly recreate the infinite-update-loop we're trying to avoid.
  const countryCode = useDataStore((s) => s.countryCode);
  const setMarketingPageSlug = useDataStore((s) => s.setMarketingPageSlug);
  const cookieCountryCode = cookies?.countryCode || DEFAULT_COUNTRY_CODE;
  const finalCountryCode = countryCode || cookieCountryCode;

  const params = useSearchParams();
  const gclid = params.get("gclid");
  // Stable dep for marketing-page effect — avoids putting the whole `props`
  // object in the dependency array (its reference changes every render in
  // dev's strict mode and would re-fire the setter forever).
  const cmsPageName = props.kind === "cms" ? props.pageName : "";

  // Hide the sticky signup affordance while the user is reading the page,
  // bring it back as soon as they scroll. Mirrors the behavior the legacy
  // /products and /features routes had.
  useEffect(() => {
    if (props.kind === "alternative-v2") return;
    if (props.kind === "graphy-vs-alternative") return;
    if (props.kind === "lms") return;
    if (props.kind === "cms") return;

    const handler = () => {
      if (!scrollY) {
        setShowSignUp(true);
      } else {
        setShowSignUp(false);
      }
    };
    window.addEventListener("scroll", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      setShowSignUp(true);
    };
  }, [props.kind, setShowSignUp]);

  // Flag the active marketing page in the global store so shared Navbar /
  // Footer can apply the marketing-page-only chrome. Kept in a tiny effect
  // with primitive deps so it never fires more than once per slug change.
  useEffect(() => {
    setMarketingPageSlug(cmsPageName);
    return () => {
      setMarketingPageSlug("");
    };
  }, [cmsPageName, setMarketingPageSlug]);

  // CMS branch — apply promotion-strip / navbar-logo overrides from the
  // payload, matching the legacy `/page/<slug>` behavior.
  useEffect(() => {
    if (props.kind !== "cms") return;
    const { pageData, navbarData } = props;
    pageData.data.forEach((data: any) => {
      if (data.type === "navbar-logo") {
        let utmFields = getUTMFieldsFromLocalStorage();
        utmFields["utm_content"] = gclid ? gclid : "autobookdemos";
        const isUrlParams = new URLSearchParams(data.href).size;
        const utmParams = stringifyUTMParams(utmFields);
        navbarData.graphyLogoOnClick = {
          href: `${data.href}${isUrlParams ? "&" : ""}${utmParams}`,
          amplitudeEvent: data.amplitudeEvent,
        };
      } else if (data.type === "promotion-strip") {
        navbarData.promotionStripData = data;
      } else if (data.type === "promotion-strip-us") {
        navbarData.promotionStripDataUS = data;
      } else if (data.type === "registration-button-us") {
        navbarData.registrationButtonUS = data;
      } else if (data.type === "registration-button") {
        navbarData.registrationButton = data;
      } else if (data.type === "navbar-logo-book") {
        navbarData.graphyLogoOnClick = {
          href: data.href,
          amplitudeEvent: data.amplitudeEvent,
        };
      }
    });
    // Mutation runs once per CMS slug — depend on the slug (primitive) and
    // gclid (string) rather than the unstable `props` object reference.
  }, [props.kind, cmsPageName, gclid]);

  if (props.kind === "graphy-vs-alternative") {
    return <GraphyVsAlternative locale={props.locale} />;
  }

  if (props.kind === "lms") {
    return <LearningManagementSystem locale={props.locale} />;
  }

  if (props.kind === "alternative-v2") {
    return (
      <AlternativeV2
        data={props.alternativeData}
        countryCode={finalCountryCode}
        useHomeChrome={props.useHomeChrome}
      />
    );
  }

  if (props.kind === "product-v2") {
    return <ProductV2 data={props.productV2} countryCode={finalCountryCode} />;
  }

  if (props.kind === "feature-v2") {
    return <ProductV2 data={props.featureV2} countryCode={finalCountryCode} />;
  }

  if (props.kind === "product-legacy") {
    const {
      productPageData,
      otherOfferingsSectionProps,
      testimonyCarouselSectionProps,
      successStoriesMarqueeSectionProps,
      navbarData,
      footerData,
    } = props;
    const {
      seoMeta,
      productHeroSectionProps,
      productStepsSectionProps,
      infoAccordionSection,
      graphyAssistProps,
      faqSectionProps,
      featureProps1,
      featuresGridProps,
      centreCtaSectionProps,
      featureProps2,
      centreCtaSectionProps2,
    } = productPageData;
    return (
      <>
        <GraphyHead {...seoMeta} countryCode={finalCountryCode} />
        <Navbar navbarData={navbarData} />
        <div className="flex flex-col gap-12 sm:gap-20">
          <ProductHeroSection {...productHeroSectionProps} />
          <TestimonyCarouselSection {...testimonyCarouselSectionProps} />
          <ProductStepsSection {...productStepsSectionProps} />
          <InfoAccordion {...infoAccordionSection} />
          {graphyAssistProps && <GraphyAssist {...graphyAssistProps} />}
          <SuccessStoriesMarqueeSection
            {...successStoriesMarqueeSectionProps}
          />
          <FaqSection {...faqSectionProps} />
          {featureProps1 && <Feature {...featureProps1} />}
          {featuresGridProps && <FeaturesGrid {...featuresGridProps} />}
          <CentreCtaSection {...centreCtaSectionProps} />
          {featureProps2 && <Feature {...featureProps2} />}
          {centreCtaSectionProps2 && (
            <CentreCtaSection {...centreCtaSectionProps2} />
          )}
          <OtherOfferingsSection {...otherOfferingsSectionProps} />
          <Footer footerData={footerData} />
        </div>
      </>
    );
  }

  if (props.kind === "feature-legacy") {
    const {
      featurePageData,
      otherOfferingsSectionProps,
      testimonyCarouselSectionProps,
      successStoriesMarqueeSectionProps,
      navbarData,
      footerData,
    } = props;
    const {
      seoMeta,
      featureHeroSectionProps,
      featureCardsSectionProps,
      infoAccordionSection,
      faqSectionProps,
      featureCTASectionProps,
    } = featurePageData;
    return (
      <>
        <GraphyHead {...seoMeta} countryCode={finalCountryCode} />
        <Navbar navbarData={navbarData} />
        <div className="flex flex-col gap-12 sm:gap-20">
          <FeatureHeroSection {...featureHeroSectionProps} />
          <FeatureCardsSection {...featureCardsSectionProps} />
          <TestimonyCarouselSection {...testimonyCarouselSectionProps} />
          <InfoAccordion {...infoAccordionSection} />
          <FaqSection {...faqSectionProps} />
          <SuccessStoriesMarqueeSection
            {...successStoriesMarqueeSectionProps}
          />
          <OtherOfferingsSection {...otherOfferingsSectionProps} />
          <FeatureCTASection {...featureCTASectionProps} />
          <Footer footerData={footerData} />
        </div>
      </>
    );
  }

  // CMS branch
  if (props.kind === "cms") {
    const { pageData, langFile, navbarData, footerData, locale } = props;
    if (!pageData) return null;
    return (
      <ErrorBoundary>
        <CreateAnimatedLP
          pageSections={pageData.data}
          meta={pageData.meta}
          scheduleDemoOnly
          langFile={langFile}
          topSubscribedGraphers={[]}
          options={undefined}
          navbarData={navbarData}
          footerData={footerData}
          locale={locale}
        />
      </ErrorBoundary>
    );
  }

  return null;
};

export default SlugPage;
